import PropTypes from 'prop-types'
import React from 'react'
import { NetworkStatus } from 'apollo-client'

import CartButton from '../CartButton/CartButton'
import { useGetCart } from '../CartQuery/CartQuery'
import Dollars from '../Dollars/Dollars'
import EmptyCart from '../EmptyCart/EmptyCart'
import Error from '../Error/Error'

import { ApprovalRules } from './ApprovalRules/ApprovalRules'
import { useApprovalRulesValidator } from './ApprovalRules/use-approval-rules-validator'
import Loader from './Cart.loader'
import CartFooter from './CartFooter/CartFooter'
import CartTable from './CartTable/CartTable'
import { useReorder } from '../Reorder/ReorderProvider/ReorderProvider'
import { CartModificationError } from './CartModificationError/CartModificationError'
import { CartUpsells } from '../Upsells/CartUpsells'
import { useRestaurant } from '../RestaurantProvider/RestaurantProvider'

import './Cart.css'

export const Cart = ({ checkoutLink, orderingAvailable, rectifyApprovalRuleLink }) => {
  const { error, loading, refetch, networkStatus, cart, warnings } = useGetCart()

  const { restaurantInfo } = useRestaurant()
  let upsellsEnabled = false

  if (
    restaurantInfo.data &&
    restaurantInfo.data.restaurant &&
    restaurantInfo.data.restaurant.upsellsConfig
  ) {
    upsellsEnabled = Boolean(restaurantInfo.data.restaurant.upsellsConfig.enabled)
  }

  const cartIsEmpty = !cart || cart.order.numberOfSelections === 0

  const { valid: approvalRulesValid } = useApprovalRulesValidator({ cart })
  const { reorderMutation: [, { loading: reorderLoading }] } = useReorder()
  const cartHasWarnings = warnings && warnings.length > 0

  const checkoutButtonEnabled = orderingAvailable && approvalRulesValid && !cartHasWarnings

  if (loading || networkStatus === NetworkStatus.refetch) {
    return <Loader />
  }

  if (error) {
    return <Error error={error} retry={refetch} />
  }

  return (
    <React.Fragment>
      <CartModificationError />
      {cartIsEmpty ? (
        <EmptyCart orderingAvailable={orderingAvailable} />
      ) : (
        <React.Fragment>
          <div styleName='long'>
            <CartTable cart={cart} editable />
          </div>
          <div styleName='sticky'>
            {upsellsEnabled && <CartUpsells />}
            <CartFooter cart={cart} deliveryChargeLabel='Delivery Estimate' />
            <ApprovalRules cart={cart} rectifyApprovalRuleLink={rectifyApprovalRuleLink} />
            <CartButton data-testid='cart-checkout-button'
              to={checkoutLink}
              left={<span>Checkout ({cart.order.numberOfSelections})</span>}
              right={<Dollars amount={cart.order.total} />}
              loading={reorderLoading}
              disabled={!checkoutButtonEnabled || reorderLoading}
            />
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

Cart.propTypes = {
  checkoutLink: PropTypes.string,
  orderingAvailable: PropTypes.bool,
  /**
   * If present, this path indicates where the user must navigate in order to rectify
   * an approval rule that is currently violated.
   */
  rectifyApprovalRuleLink: PropTypes.string
}

export default React.memo(Cart)
